import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ChannelBadge } from "@/components/ChannelBadge";
import { toast } from "sonner";
import { Briefcase, MapPin, Clock, Users, Check, ShoppingBag } from "lucide-react";

export default function ManagerOpenRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketplace/open-requests");
      setRequests(r.data?.requests || []);
    } catch (e) {
      toast.error("Errore caricamento opportunità");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const apply = async (id) => {
    try {
      await api.post(`/marketplace/open-requests/${id}/apply`, { note });
      toast.success("Candidatura inviata");
      setApplying(null);
      setNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <ShoppingBag size={24} className="text-[#2D6BFF]" /> Opportunità marketplace
          </h1>
          <p className="mt-1 text-slate-600 text-sm">Richieste aperte: candidati direttamente. CXOORA modera prima di pubblicare la shortlist.</p>
        </div>
        <Badge className="bg-[#EAF0FF] text-[#2D6BFF] border-0">{requests.length} aperte</Badge>
      </div>

      {loading ? (
        <p className="text-center text-slate-500 py-8">Caricamento...</p>
      ) : requests.length === 0 ? (
        <div className="cxo-card p-12 text-center text-slate-500">
          <Briefcase size={36} className="mx-auto text-slate-300" />
          <p className="mt-3 font-medium">Nessuna opportunità aperta al momento.</p>
          <p className="text-sm">Le nuove richieste appariranno qui.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {requests.map((r) => (
            <div key={r.id} className="cxo-card p-5 flex flex-col" data-testid={`opp-card-${r.id}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <ChannelBadge channel="marketplace" size="xs" />
                  <h3 className="mt-2 font-display font-semibold text-slate-900">{r.title}</h3>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-3">{r.summary || "—"}</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-slate-600">
                <Cell icon={Briefcase} label={r.recommended_role || "—"} />
                <Cell icon={MapPin} label={r.region || "—"} />
                <Cell icon={Clock} label={r.urgency ? `Urgenza ${r.urgency}` : "—"} />
                <Cell icon={Users} label={`${r.applicants_count} candidati`} />
              </div>
              {r.budget_range && <p className="mt-3 text-xs text-slate-500"><strong>Budget:</strong> {r.budget_range}</p>}
              <div className="mt-4 pt-4 border-t border-slate-100">
                {r.already_applied ? (
                  <Badge className="bg-[#E9F8F1] text-[#1F9D6A] border-0 gap-1.5"><Check size={12} /> Candidatura inviata</Badge>
                ) : (
                  <Dialog open={applying === r.id} onOpenChange={(o) => { setApplying(o ? r.id : null); setNote(""); }}>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-[#2D6BFF] text-white gap-1.5" data-testid={`apply-btn-${r.id}`}>
                        Candidati
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Candidati a "{r.title}"</DialogTitle></DialogHeader>
                      <p className="text-sm text-slate-600">Aggiungi una breve nota (max 500 caratteri) per spiegare perché sei un buon match.</p>
                      <Textarea rows={4} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Es. Ho già seguito 3 PMI manifatturiere nel digital ops..." data-testid="apply-note-input" />
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setApplying(null)}>Annulla</Button>
                        <Button onClick={() => apply(r.id)} className="bg-[#2D6BFF] text-white" data-testid="apply-confirm-btn">Invia candidatura</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Cell = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-1.5">
    <Icon size={12} className="text-slate-400 shrink-0" />
    <span className="truncate">{label}</span>
  </div>
);
